/**
 * Period-comparison utilities for the dashboard.
 *
 * Each `PeriodMode` describes a "current" range and a "previous" range to
 * compare against (this week vs last week, this month vs same month last
 * year, etc.). All ranges are returned as half-open intervals
 * `[start, end)` of YYYY-MM-DD strings, which compose with the existing
 * widget filters that compare against `docDate` / `createdAt` ISO strings.
 */

export type PeriodMode =
  | 'day-vs-yesterday'
  | 'day-vs-last-year'
  | 'week-vs-last-week'
  | 'week-vs-last-year'
  | 'month-vs-last-month'
  | 'month-vs-last-year'
  | 'year-vs-last-year';

export const PERIOD_MODES: readonly PeriodMode[] = [
  'day-vs-yesterday',
  'day-vs-last-year',
  'week-vs-last-week',
  'week-vs-last-year',
  'month-vs-last-month',
  'month-vs-last-year',
  'year-vs-last-year',
] as const;

export const DEFAULT_PERIOD_MODE: PeriodMode = 'month-vs-last-month';

export interface DateRange {
  /** Inclusive YYYY-MM-DD. */
  start: string;
  /** Exclusive YYYY-MM-DD (the day AFTER the last day in the range). */
  end: string;
}

export interface ComparisonRanges {
  current: DateRange;
  previous: DateRange;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}

function addMonths(d: Date, months: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + months);
  return r;
}

function addYears(d: Date, years: number): Date {
  const r = new Date(d);
  r.setFullYear(r.getFullYear() + years);
  return r;
}

/** Monday as the first day of the week (ISO 8601). */
function startOfWeek(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = r.getDay(); // 0 = Sunday … 6 = Saturday
  const diff = day === 0 ? -6 : 1 - day;
  r.setDate(r.getDate() + diff);
  return r;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function getComparisonRanges(
  mode: PeriodMode,
  now: Date = new Date(),
): ComparisonRanges {
  switch (mode) {
    case 'day-vs-yesterday': {
      const today = startOfDay(now);
      const yesterday = addDays(today, -1);
      return {
        current: { start: isoDate(today), end: isoDate(addDays(today, 1)) },
        previous: { start: isoDate(yesterday), end: isoDate(today) },
      };
    }
    case 'day-vs-last-year': {
      const today = startOfDay(now);
      const sameDayLastYear = addYears(today, -1);
      return {
        current: { start: isoDate(today), end: isoDate(addDays(today, 1)) },
        previous: {
          start: isoDate(sameDayLastYear),
          end: isoDate(addDays(sameDayLastYear, 1)),
        },
      };
    }
    case 'week-vs-last-week': {
      const wkStart = startOfWeek(now);
      const lastStart = addDays(wkStart, -7);
      return {
        current: { start: isoDate(wkStart), end: isoDate(addDays(wkStart, 7)) },
        previous: { start: isoDate(lastStart), end: isoDate(wkStart) },
      };
    }
    case 'week-vs-last-year': {
      // Shift by 364 days (52 weeks) to land on the same weekday last year.
      const wkStart = startOfWeek(now);
      const lastYearStart = addDays(wkStart, -364);
      return {
        current: { start: isoDate(wkStart), end: isoDate(addDays(wkStart, 7)) },
        previous: {
          start: isoDate(lastYearStart),
          end: isoDate(addDays(lastYearStart, 7)),
        },
      };
    }
    case 'month-vs-last-month': {
      const mStart = startOfMonth(now);
      const lastStart = addMonths(mStart, -1);
      return {
        current: { start: isoDate(mStart), end: isoDate(addMonths(mStart, 1)) },
        previous: { start: isoDate(lastStart), end: isoDate(mStart) },
      };
    }
    case 'month-vs-last-year': {
      const mStart = startOfMonth(now);
      const lastYearStart = addYears(mStart, -1);
      return {
        current: { start: isoDate(mStart), end: isoDate(addMonths(mStart, 1)) },
        previous: {
          start: isoDate(lastYearStart),
          end: isoDate(addMonths(lastYearStart, 1)),
        },
      };
    }
    case 'year-vs-last-year': {
      // Year-to-date vs the same window last year (Jan 1 → today).
      const yStart = startOfYear(now);
      const tomorrow = addDays(startOfDay(now), 1);
      const lastYStart = addYears(yStart, -1);
      const lastEnd = addYears(tomorrow, -1);
      return {
        current: { start: isoDate(yStart), end: isoDate(tomorrow) },
        previous: { start: isoDate(lastYStart), end: isoDate(lastEnd) },
      };
    }
  }
}

/** Half-open membership test: `start <= value < end`. */
export function inRange(value: string, range: DateRange): boolean {
  return value >= range.start && value < range.end;
}
